import React, { ReactNode } from 'react';
import { Label } from 'reactstrap';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import classnames from 'classnames';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import Input from '../../components/Input/Input';
import {
  compose,
  withStateHandlers,
  StateHandlerMap,
  StateHandler,
  withHandlers,
  lifecycle,
} from 'recompose';
import { LifeCycleThis } from '@mm/utils';
import { ModalHeader, ModalBody, ModalFooter } from '../modal-helpers';
import { ENTER_CODE } from '@mm/constants/keys-codes';
import CompanyModel from '@mm/state/model/CompanyModel';
import CompanyCRADActions from '@mm/state/actions/CompanyCRUDActions';
import UsersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import './CreateCompanyModal.scss';

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsFromMatch = ReturnType<typeof mapStateToProps>;
type StateProps = {
  companyName: string;
  inputNode: any;
  error: string;
};

type StateHandlerProps = StateHandlerMap<StateProps> & {
  setCompanyName(value: string): StateHandler<StateProps>;
  setInputNode(input: ReactNode): StateHandler<StateProps>;
  setError(error: string | null): StateHandler<StateProps>;
};

type PropsBeforeHandlers = OuterProps &
  StateProps &
  StateHandlerProps &
  PropsFromMatch &
  PropsFromDispatch;

type PropsBeforeKeyDown = PropsBeforeHandlers & {
  onNameChange: OnNameChange;
  onCreateCompany: OnCreateCompany;
};

type OnNameChange = (props: PropsBeforeHandlers) => (e: any) => void;
type OnCreateCompany = (props: PropsBeforeHandlers) => (e: any) => void;
type SetRef = (props: PropsBeforeHandlers) => (node: ReactNode) => void;
type OnKeyDown = (props: PropsBeforeKeyDown) => (e: any) => void;

type HandlersProps = {
  onNameChange: OnNameChange;
  onCreateCompany: OnCreateCompany;
  onKeyDown: OnKeyDown;
  setRef: SetRef;
};

type HandleProps = {
  onKeyDown: ReturnType<OnKeyDown>;
  setRef: ReturnType<SetRef>;
};

type OuterProps = {
  companies: CompanyModel[];
};

type InnerProps = StateProps &
  StateHandlerProps &
  HandlersProps &
  PropsFromMatch &
  PropsFromDispatch;

type Props = OuterProps & InnerProps & HandleProps;

const mapStateToProps = (state: StoreState) => ({
  session: state.session.session,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  createCompany: (company: { name: string }) =>
    dispatch(CompanyCRADActions.create(company)),
  activateCompany: (id, entityId) =>
    dispatch(UsersCRUDActions.updateActiveCompanyId(entityId, id)),
});

const initialState = {
  companyName: '',
  inputNode: null,
  error: null,
};

const stateUpdaters = {
  setCompanyName: () => companyName => ({ companyName }),
  setInputNode: () => inputNode => ({ inputNode }),
  setError: () => error => ({ error }),
};

const onNameChange: OnNameChange =
  ({ setCompanyName, error, setError }) =>
  ({ target: { value } }) => {
    if (error) setError(null);
    setCompanyName(value);
  };

const onCreateCompany: OnCreateCompany = props => async e => {
  const {
    createCompany,
    companyName,
    closeModal,
    companies,
    setError,
    activateCompany,
    session,
  } = props;
  const existingCompany = companies.find(
    company => company.name === companyName,
  );
  if (!existingCompany) {
    const companyCreated = await createCompany({ name: companyName });
    if (companyCreated) {
      activateCompany(companyCreated.company.id, session.id);
      closeModal();
      setError(null);
    } else {
      setError('Failed to create company');
    }
  } else {
    setError('Company with this name exists');
  }
};

const setRef: SetRef =
  ({ setInputNode }) =>
  node => {
    setInputNode(node);
  };

function componentDidMount(this: LifeCycleThis<Props>) {
  const { onKeyDown, inputNode } = this.props;
  if (inputNode) inputNode.focus();
  document.addEventListener('keydown', onKeyDown);
}

function componentDidUpdate(this: LifeCycleThis<Props>, prevProps) {
  const { inputNode } = this.props;
  if (inputNode !== prevProps.inputNode) {
    inputNode.input.focus();
  }
}

function componentWillUnmount(this: LifeCycleThis<Props>) {
  const { onKeyDown } = this.props;
  document.removeEventListener('keydown', onKeyDown);
}

const onKeyDown: OnKeyDown =
  ({ onCreateCompany, companyName }) =>
  e => {
    if (e.keyCode === ENTER_CODE && companyName.length) {
      onCreateCompany(e);
    }
  };

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ onNameChange, onCreateCompany, setRef }),
  withHandlers({ onKeyDown }),
  lifecycle<Props, {}>({
    componentDidMount,
    componentWillUnmount,
    componentDidUpdate,
  }),
);

const CreateCompanyModal = (props: Props) => {
  const { companyName, onNameChange, onCreateCompany, setRef, error } = props;
  const isDisabled =
    companyName.length === 0 || !companyName.trim() || /^\s/.test(companyName);
  const InputClass = classnames('create-company-modal__input', {
    'create-company-modal__input--error': error,
  });

  return (
    <div className="create-company-modal">
      <ModalHeader className="create-company-modal__header">
        Create Company
      </ModalHeader>
      <ModalBody className="create-company-modal__body">
        <Label className="create-company-modal__input-wrapper">
          <span className="create-company-modal__input-label">
            Company Name
          </span>
          <div className="create-company-modal__input-with-error">
            <Input
              value={companyName}
              className={InputClass}
              placeholder="Enter Company Name"
              maxLength="50"
              onChange={onNameChange}
              ref={setRef}
            />
            {error && (
              <div className="create-company-modal__error">{error}</div>
            )}
          </div>
        </Label>
      </ModalBody>
      <ModalFooter className="create-company-modal__footer">
        <Button
          className="create-company-modal__action"
          onClick={onCreateCompany}
          disabled={isDisabled}
        >
          Create Company
        </Button>
      </ModalFooter>
    </div>
  );
};

export default enhance(CreateCompanyModal);
